import { useData } from '@happyspace/sdk/data';

// ─── Default category definitions ───
// These are the canonical list. iconType: "svg" means the icon is an inline
// SVG component (defined in HomeSections / ViewAllCategories). iconType: "image"
// means the icon is an <img> with a URL that can be overridden by the admin.

export interface CategoryDef {
  name: string;
  href: string;
  iconType: "svg" | "image";
  iconUrl?: string; // only for image-type icons; the default URL
}

export const DEFAULT_CATEGORIES: CategoryDef[] = [
  { name: "Restaurants", href: "/restaurants", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164199-6yn4hj.png" },
  { name: "Concerts/Events", href: "/concerts", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859810-lpw44o.png" },
  { name: "Temples", href: "/temples", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338860170-7my59.png" },
  { name: "Shopping", href: "/shopping", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164009-pppzhi.png" },
  { name: "Lawyers", href: "/lawyers", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340164180-31av45.png" },
  { name: "Health & Wellness", href: "/health-wellness", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780340319237-4x8nm.png" },
  { name: "Influencers", href: "/influencers", iconType: "image", iconUrl: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780338859925-bo6iq.png" },
  { name: "Movies", href: "/movies", iconType: "svg" },
  { name: "Realtors", href: "/realtors", iconType: "svg" },
  { name: "Party Venues", href: "/party-venues", iconType: "svg" },
   { name: "Photo/Video", href: "/photo-video", iconType: "svg" },
  { name: "Event Planning", href: "/event-management", iconType: "svg" },
  { name: "Marketing/production", href: "/event-production", iconType: "svg" },
  { name: "Grocery", href: "/grocery", iconType: "svg" },
  { name: "Dessert Shops", href: "/dessert-shops", iconType: "svg" },
  { name: "Cafe", href: "/cafe", iconType: "svg" },
  { name: "Food Trucks", href: "/restaurants", iconType: "svg" },
  { name: "Hookah Lounges", href: "/hookah-lounges", iconType: "svg" },
  { name: "Beauty Services", href: "/BeautyServices", iconType: "svg" },
  { name: "Salon/Spa", href: "/salon-spa", iconType: "svg" },
  { name: "DJ's", href: "/djs", iconType: "svg" },
  { name: "Kids", href: "/kids", iconType: "svg" },
  { name: "Catering", href: "/catering-only", iconType: "svg" },
  { name: "Home Services", href: "/home-services", iconType: "svg" },
  { name: "Travel Agents", href: "/travel-agents", iconType: "svg" },
  { name: "Performing Artists", href: "/performing-artists", iconType: "svg" },
  { name: "Media", href: "/media", iconType: "svg" },
  { name: "Senior Citizens", href: "/senior-citizen", iconType: "svg" },
  { name: "Start Ups", href: "/start-ups", iconType: "svg" },
  { name: "Consulting", href: "/it-consulting", iconType: "svg" },
  { name: "Karaoke Spots", href: "/karaoke-spots", iconType: "svg" },
  { name: "Financial Services", href: "/financial-services", iconType: "svg" },
  { name: "Insurance", href: "/insurance", iconType: "svg" },
  { name: "Others", href: "/others", iconType: "svg" },
  // Additional categories from ViewAllCategories
  { name: "Bartenders", href: "/bartenders", iconType: "svg" },
  { name: "Bakers", href: "/bakers", iconType: "svg" },
  { name: "Dating/Matrimonial", href: "/dating-matrimonial", iconType: "svg" },
  { name: "Non Profit Org", href: "/non-profit-org", iconType: "svg" },
  { name: "Youth", href: "/youth", iconType: "svg" },
  { name: "Women", href: "/women", iconType: "svg" },
  { name: "Cricket", href: "/cricket", iconType: "svg" },
  { name: "Tailoring", href: "/tailoring", iconType: "svg" },
  { name: "Automotive Services", href: "/automotive-services", iconType: "svg" },
  { name: "Jobs", href: "/jobs", iconType: "svg" },
];

// ─── Legacy category-name normalization ───
// Rows in the live mc_* collections may still carry pre-rename category
// strings. Normalize them at every render/filter point so old data displays
// (and filters) under the new category name.
const LEGACY_CATEGORY_NAMES: Record<string, string> = {
  "Sweet Temptations": "Dessert Shops",
};

export function normalizeCategoryName(name?: string | null): string {
  if (!name) return "";
  return LEGACY_CATEGORY_NAMES[name.trim()] ?? name;
}

// ─── Admin override record shape ───
export interface CategoryOverride {
  name: string;        // matches CategoryDef.name
  hidden?: boolean;
  iconUrl?: string;    // override icon URL (for image-type icons)
  deleted?: boolean;
}

// ─── Hook: read admin overrides from the data store ───
export function useCategoryOverrides() {
  const { rows, loading } = useData("category_icons", { autoFetch: true, limit: 200 });

  // Build lookup maps
  const overrideMap = new Map<string, CategoryOverride>();
  const hiddenSet = new Set<string>();
  const deletedSet = new Set<string>();
  const iconUrlOverrides = new Map<string, string>();

  if (rows && !loading) {
    for (const row of rows as any[]) {
      const name = row.name || row.categoryName || "";
      overrideMap.set(name, row as CategoryOverride);
      if (row.hidden) hiddenSet.add(name);
      if (row.deleted) deletedSet.add(name);
      if (row.iconUrl) iconUrlOverrides.set(name, row.iconUrl);
    }
  }

  return { overrideMap, hiddenSet, deletedSet, iconUrlOverrides, loading };
}

// ─── Helper: get the effective list of visible categories ───
export function getVisibleCategories(
  defaults: CategoryDef[],
  hiddenSet: Set<string>,
  deletedSet: Set<string>,
  iconUrlOverrides: Map<string, string>,
): CategoryDef[] {
  return defaults
    .filter((c) => !deletedSet.has(c.name) && !hiddenSet.has(c.name))
    .map((c) => {
      if (c.iconType === "image" && iconUrlOverrides.has(c.name)) {
        return { ...c, iconUrl: iconUrlOverrides.get(c.name) };
      }
      return c;
    });
}
